/**
 * Calendar module — redesigned (iter 10) with sub-tabs.
 *
 * Sub-tabs:
 *  01 Año 1 (Kanban) — annual milestones organised by quarter columns
 *     (drag-friendly column layout, edit in place).
 *  02 Plan 5 años — long-horizon goals by year (linear list).
 *
 * Existing data shape preserved: { annual_milestones[], five_year_plan[] }.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Trash2,
  CalendarRange,
  Rocket,
} from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import {
  SectionCard,
  FieldLabel,
  TextInput,
  TextArea,
  PrimaryButton,
  SecondaryButton,
  AIBadge,
  Overline,
  SubTabs,
} from "@/components/ui-kit";
import AIAssistant from "@/components/AIAssistant";

const empty = { annual_milestones: [], five_year_plan: [] };
const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];
const QUARTER_COLORS = {
  Q1: "#3A5A6B",
  Q2: "#2563EB",
  Q3: "#B89154",
  Q4: "#9E2A2B",
};

export default function CalendarModule({ data, project, onSave }) {
  const { t, lang } = useLang();
  const [v, setV] = useState({ ...empty, ...(data || {}) });
  const [tab, setTab] = useState("annual");
  const [quarterFilter, setQuarterFilter] = useState("all"); // "all" | "Q1"..."Q4"
  useEffect(() => {
    setV((prev) => {
    const next = { ...empty, ...(data || {}) };
    try { if (JSON.stringify(prev) === JSON.stringify(next)) return prev; } catch (e) { void e; }
    return next;
  });
  }, [data]);
  useEffect(() => {
    onSave?.(v);
  }, [v, onSave]);

  const addMilestone = (quarter = "Q1") =>
    setV((p) => ({ ...p, annual_milestones: [...(p.annual_milestones || []), { quarter, title: "", description: "" }] }));
  const updateMilestone = (i, k, val) =>
    setV((p) => ({ ...p, annual_milestones: p.annual_milestones.map((x, idx) => (idx === i ? { ...x, [k]: val } : x)) }));
  const removeMilestone = (i) =>
    setV((p) => ({ ...p, annual_milestones: p.annual_milestones.filter((_, idx) => idx !== i) }));

  const addYear = () => {
    const nextYear = (v.five_year_plan?.length || 0) + 1;
    setV((p) => ({ ...p, five_year_plan: [...(p.five_year_plan || []), { year: (lang === "es" ? `Año ${nextYear}` : `Year ${nextYear}`), goals: "" }] }));
  };
  const updateYear = (i, k, val) =>
    setV((p) => ({ ...p, five_year_plan: p.five_year_plan.map((x, idx) => (idx === i ? { ...x, [k]: val } : x)) }));
  const removeYear = (i) =>
    setV((p) => ({ ...p, five_year_plan: p.five_year_plan.filter((_, idx) => idx !== i) }));

  // Index milestones for the kanban view (preserve original index for editing)
  const milestonesByQuarter = useMemo(() => {
    const out = { Q1: [], Q2: [], Q3: [], Q4: [] };
    (v.annual_milestones || []).forEach((m, idx) => {
      const q = QUARTERS.includes(m.quarter) ? m.quarter : "Q1";
      out[q].push({ ...m, _idx: idx });
    });
    return out;
  }, [v.annual_milestones]);

  const suggestions = lang === "es"
    ? [
        "Propón los 8 hitos más importantes para el primer año (por trimestre)",
        "Diseña un plan a 5 años con objetivos por año",
        "Sugiere hitos OKR-style con métricas concretas",
      ]
    : [
        "Propose the 8 most important milestones for year 1 (by quarter)",
        "Design a 5-year plan with yearly goals",
        "Suggest OKR-style milestones with concrete metrics",
      ];

  const tabs = [
    { key: "annual",     label: lang === "es" ? "Año 1 — Hitos por trimestre" : "Year 1 — Quarterly milestones", icon: CalendarRange },
    { key: "long_term",  label: lang === "es" ? "Plan a 5 años"               : "5-year plan",                   icon: Rocket },
  ];

  // Quarters visible given the current filter (memo avoids filter() in JSX).
  const visibleQuarters = useMemo(
    () => QUARTERS.filter((q) => quarterFilter === "all" || quarterFilter === q),
    [quarterFilter]
  );

  return (
    <>
      <SectionCard
        title={t("module.calendar")}
        subtitle={lang === "es" ? "Roadmap del primer año y horizonte a 5 años." : "Year 1 roadmap and 5-year horizon."}
        action={
          <div className="flex items-center gap-3">
            <AIBadge />
            <AIAssistant module="calendar" context={{ project: project?.name }} suggestions={suggestions} />
          </div>
        }
        testId="calendar-section"
      >
        <SubTabs tabs={tabs} active={tab} onChange={setTab} testIdPrefix="calendar-subtab" />

        {tab === "annual" && (
          <div data-testid="calendar-tab-annual" className="space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-[#5C5B5A] max-w-2xl">
                {lang === "es"
                  ? "Cada hito ocupa toda la fila para que puedas leerlo y editarlo con espacio. Cambia el trimestre desde la píldora de la izquierda. Filtra por trimestre con las pestañas de abajo si quieres ver solo uno."
                  : "Each milestone uses the full row width so it's easy to read and edit. Switch the quarter from the pill on the left. Use the chips below to filter by quarter."}
              </p>
              <SecondaryButton onClick={() => addMilestone(quarterFilter === "all" ? "Q1" : quarterFilter)} testId="add-milestone-btn">
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                {t("calendar.add_milestone")}
              </SecondaryButton>
            </div>

            {/* Quarter filter chips */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="quarter-filter">
              {[{ key: "all", label: lang === "es" ? "Todos" : "All", count: (v.annual_milestones || []).length, color: "#1A1A1A" },
                ...QUARTERS.map((q) => ({
                  key: q, label: q, count: milestonesByQuarter[q].length, color: QUARTER_COLORS[q],
                }))].map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setQuarterFilter(f.key)}
                  data-testid={`quarter-filter-${f.key}`}
                  className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] px-3 py-1.5 rounded-sm border transition-colors ${
                    quarterFilter === f.key
                      ? "border-transparent text-[#F9F8F6]"
                      : "border-[#E8E6E1] bg-white text-[#5C5B5A] hover:text-[#1A1A1A]"
                  }`}
                  style={quarterFilter === f.key ? { backgroundColor: f.color } : {}}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: quarterFilter === f.key ? "#F9F8F6" : f.color }} />
                  {f.label}
                  <span className="font-mono opacity-70" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>({f.count})</span>
                </button>
              ))}
            </div>

            {/* Milestones — full-width horizontal rows, grouped by quarter */}
            <div className="space-y-7" data-testid="milestones-list">
              {visibleQuarters.map((q) => {
                const items = milestonesByQuarter[q] || [];
                if (quarterFilter === "all" && items.length === 0) return null;
                return (
                  <section key={q} data-testid={`quarter-group-${q}`}>
                    {quarterFilter === "all" && (
                      <header className="flex items-center justify-between mb-3 pb-2 border-b border-[#E8E6E1]">
                        <div className="flex items-center gap-2">
                          <span className="w-1 h-4 rounded-sm" style={{ backgroundColor: QUARTER_COLORS[q] }} />
                          <span className="text-xs uppercase tracking-[0.2em] font-medium" style={{ color: QUARTER_COLORS[q] }}>
                            {q} · {lang === "es" ? "Trimestre" : "Quarter"}
                          </span>
                          <span className="text-[10px] text-[#8A8886] font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                            ({items.length})
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => addMilestone(q)}
                          className="inline-flex items-center gap-1 text-xs text-[#5C5B5A] hover:text-[#1A1A1A]"
                          data-testid={`add-milestone-${q}-btn`}
                        >
                          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                          {lang === "es" ? "Añadir a " : "Add to "}{q}
                        </button>
                      </header>
                    )}
                    {items.length === 0 ? (
                      <p className="text-sm text-[#8A8886] py-4 text-center border border-dashed border-[#E8E6E1] rounded-sm">
                        {lang === "es" ? `Sin hitos en ${q}.` : `No milestones in ${q}.`}
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {items.map((m) => {
                          const i = m._idx;
                          return (
                            <div
                              key={i}
                              className="bg-white border border-[#E8E6E1] rounded-sm p-4 flex gap-4 items-stretch"
                              style={{ borderLeftWidth: 4, borderLeftColor: QUARTER_COLORS[m.quarter] }}
                              data-testid={`milestone-${i}`}
                            >
                              {/* Quarter selector — left */}
                              <div className="flex flex-col items-center gap-2 pt-1 shrink-0">
                                <select
                                  value={m.quarter}
                                  onChange={(e) => updateMilestone(i, "quarter", e.target.value)}
                                  className="text-xs uppercase tracking-[0.18em] font-semibold border border-[#E8E6E1] bg-[#FBFAF7] rounded-sm px-2 py-1.5 outline-none focus:border-[#2563EB] cursor-pointer"
                                  style={{ color: QUARTER_COLORS[m.quarter], minWidth: 64 }}
                                  data-testid={`milestone-${i}-quarter`}
                                >
                                  {QUARTERS.map((q2) => (
                                    <option key={q2} value={q2}>{q2}</option>
                                  ))}
                                </select>
                              </div>

                              {/* Body — center, takes all remaining width */}
                              <div className="flex-1 min-w-0 space-y-2">
                                <TextInput
                                  value={m.title}
                                  onChange={(x) => updateMilestone(i, "title", x)}
                                  placeholder={lang === "es" ? "Título del hito (ej. Constitución de la sociedad)" : "Milestone title (e.g. Company incorporation)"}
                                  testId={`milestone-${i}-title`}
                                />
                                <TextArea
                                  value={m.description}
                                  onChange={(x) => updateMilestone(i, "description", x)}
                                  rows={2}
                                  placeholder={lang === "es" ? "Detalles, responsables, métricas de éxito..." : "Details, owners, success metrics..."}
                                  testId={`milestone-${i}-desc`}
                                />
                              </div>

                              {/* Delete — right */}
                              <button
                                type="button"
                                onClick={() => removeMilestone(i)}
                                className="shrink-0 self-start text-[#8A8886] hover:text-[#9E2A2B] p-1"
                                data-testid={`milestone-${i}-remove`}
                                aria-label="remove"
                              >
                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </section>
                );
              })}
              {(v.annual_milestones || []).length === 0 && (
                <div className="text-sm text-[#8A8886] py-10 text-center border border-dashed border-[#E8E6E1] rounded-sm">
                  {lang === "es" ? "Sin hitos. Añade el primero con el botón de arriba." : "No milestones. Add the first one above."}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "long_term" && (
          <div data-testid="calendar-tab-long-term" className="space-y-5">
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#5C5B5A]">
                {lang === "es"
                  ? "Visión a 5 años: define el objetivo principal de cada año (revenue, expansión, equipo, producto)."
                  : "5-year vision: define the main goal of each year (revenue, expansion, team, product)."}
              </p>
              <SecondaryButton onClick={addYear} testId="add-year-btn">
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                {t("calendar.add_year")}
              </SecondaryButton>
            </div>
            {(v.five_year_plan || []).length === 0 ? (
              <div className="text-sm text-[#8A8886] py-6 text-center border border-dashed border-[#E8E6E1] rounded-sm">
                {lang === "es" ? "Sin años planificados. Añade el primero." : "No years planned yet. Add the first one."}
              </div>
            ) : (
              <div className="space-y-3">
                {v.five_year_plan.map((y, i) => (
                  <div key={i} className="border border-[#E8E6E1] rounded-sm p-5 bg-white" data-testid={`year-${i}`}>
                    <div className="flex items-baseline gap-3 mb-3">
                      <span className="font-mono text-xs text-[#8A8886]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <TextInput value={y.year} onChange={(x) => updateYear(i, "year", x)} testId={`year-${i}-name`} />
                    </div>
                    <FieldLabel>{t("calendar.goals")}</FieldLabel>
                    <TextArea value={y.goals} onChange={(x) => updateYear(i, "goals", x)} rows={4} testId={`year-${i}-goals`} />
                    <div className="mt-3 flex justify-end">
                      <button onClick={() => removeYear(i)} className="text-[#8A8886] hover:text-[#9E2A2B] text-sm inline-flex items-center gap-1" data-testid={`year-${i}-remove`}>
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} /> {t("common.delete")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </SectionCard>

    </>
  );
}
